<template>
	<header class="header">
		<div class="header__bar">
			<output
				v-if="isLive"
				aria-label="Live"
				class="header__live-pill body-xs"
			>
				<span class="header__live-pill-dot" />
				{{ t('header.live') }}
			</output>

			<img
				src="../assets/voicemod-logo.png"
				alt="Voicemod"
				class="header__logo header__logo--big"
			/>
			<HYIcon
				class="header__logo header__logo--small"
				name="VoicemodLogo"
				size="lg"
			/>

			<AudioControls
				class="header__audio-controls"
				:volume="volume"
				@onChangeVolume="handleChangeVolume"
				@onToggleMute="handleToggleMute"
			/>
		</div>

		<h2
			v-if="!isLoading"
			class="body-md header__description"
		>
			{{ description }}
		</h2>
	</header>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { HYIcon } from '@voicemod1/harmony-ui';

import AudioControls from '../components/AudioControls.vue';
import { useStore } from '../store';

const { t } = useI18n();
const store = useStore();
const { isLoading, volume, defaultTimeSeconds, isLive } = storeToRefs(store);

const description = computed(() => {
	return isLive.value
		? t('header.title', { time: `${defaultTimeSeconds.value}s` })
		: t('header.title.offline');
});

const handleChangeVolume = (volume: number) => {
	store.volumeChanged(volume);
};

const handleToggleMute = () => {
	store.muteToggled();
};
</script>

<style lang="scss" scoped>
.header {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	background: var(--hy-color-surface-neutral-deep);
	box-shadow: 0 0 10px 0 rgb(0 0 0 / 100%);

	&__bar {
		position: relative;

		display: flex;
		align-items: center;
		justify-content: center;

		width: 100%;
		height: 56px;
	}

	&__live-pill {
		position: absolute;
		top: var(--hy-space-md);
		left: var(--hy-space-md);

		display: flex;
		gap: 6px;
		align-items: center;
		justify-content: center;

		height: 24px;
		padding: 0 8px;

		color: var(--hy-color-text-secondary);

		background-color: var(--hy-color-surface-neutral-strong);
		border-radius: 34px;
	}

	&__audio-controls {
		position: absolute;
		right: var(--hy-space-md);
		margin-top: -4px;
	}

	&__live-pill-dot {
		width: 8px;
		height: 8px;
		background: var(--hy-color-surface-brand-default);
		border-radius: 50%;
	}

	&__logo {
		width: 150px;

		&--small {
			display: none;
			height: 56px;
			color: var(--hy-color-text-brand);
		}

		&--big {
			display: block;
		}

		@media (width < 280px) {
			&--small {
				display: block;
			}

			&--big {
				display: none;
			}
		}
	}

	&__description {
		margin: 0;
		padding: var(--hy-space-xs);
		color: var(--hy-color-text-secondary);
	}
}
</style>
